// src/pages/AdminManagement.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Users,
  ArrowLeft,
  UserPlus,
  Shield,
  ShieldAlert,
  Loader2,
  Trash2,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Key,
  RefreshCw
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { adminManagementAPI } from '@/services/api';

interface Admin {
  id: number;
  username: string;
  role: 'SUPER_ADMIN' | 'ADMIN';
  isActive: boolean;
  lastLogin?: string;
  failedLoginAttempts: number;
  accountLockedUntil?: string;
  createdAt: string;
}

export default function AdminManagement() {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [revokeConfirm, setRevokeConfirm] = useState<Admin | null>(null);
  
  const [newAdmin, setNewAdmin] = useState({
    username: '',
    password: '',
    role: 'ADMIN' as 'SUPER_ADMIN' | 'ADMIN'
  });
  const [isCreating, setIsCreating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();
  const { role, username } = useAuth();

  // Check if user is SUPER_ADMIN
  const isSuperAdmin = role === 'SUPER_ADMIN';

  useEffect(() => {
    if (!isSuperAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only SUPER_ADMIN can access this page',
        variant: 'destructive',
      });
      navigate('/admin/dashboard');
      return;
    }
    fetchAdmins();
  }, [isSuperAdmin, navigate]);

  const fetchAdmins = async (refresh = false) => {
    try {
      if (refresh) setIsRefreshing(true);
      else setIsLoading(true);

      const response = await adminManagementAPI.listAdmins();
      setAdmins(response || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load admins',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdmin.username || !newAdmin.password) {
      toast({
        title: 'Validation Error',
        description: 'Username and password are required',
        variant: 'destructive',
      });
      return;
    }

    if (newAdmin.password.length < 8) {
      toast({
        title: 'Validation Error',
        description: 'Password must be at least 8 characters',
        variant: 'destructive',
      });
      return;
    }

    setIsCreating(true);

    try {
      await adminManagementAPI.createAdmin(newAdmin);
      
      toast({
        title: 'Success',
        description: `Admin '${newAdmin.username}' created successfully`,
      });

      setCreateModalOpen(false);
      setNewAdmin({ username: '', password: '', role: 'ADMIN' });
      fetchAdmins(true);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create admin',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevokeAdmin = async () => {
    if (!revokeConfirm) return;

    setIsRevoking(true);

    try {
      await adminManagementAPI.revokeAdmin(revokeConfirm.username);
      
      toast({
        title: 'Success',
        description: `Admin '${revokeConfirm.username}' has been revoked`,
      });

      setRevokeConfirm(null);
      fetchAdmins(true);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to revoke admin',
        variant: 'destructive',
      });
    } finally {
      setIsRevoking(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-KE', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Africa/Nairobi'
      });
    } catch {
      return 'Invalid date';
    }
  };

  if (!isSuperAdmin) {
    return null;
  }

  if (isLoading && admins.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto" />
          <p className="text-gray-600 dark:text-gray-400">Loading admin management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3 mb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/dashboard')}
              className="p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-bold text-lg text-gray-900 dark:text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-teal-500" />
                Admin Management
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Create and manage admin accounts
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchAdmins(true)}
                disabled={isRefreshing}
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                size="sm"
                onClick={() => setCreateModalOpen(true)}
                className="bg-teal-500 hover:bg-teal-600"
              >
                <UserPlus className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">New Admin</span>
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2">
            <Card>
              <CardContent className="p-3">
                <p className="text-xs text-gray-600 dark:text-gray-400">Total</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {admins.length}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-xs text-gray-600 dark:text-gray-400">Active</p>
                <p className="text-xl font-bold text-green-600">
                  {admins.filter(a => a.isActive).length}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-xs text-gray-600 dark:text-gray-400">Super</p>
                <p className="text-xl font-bold text-purple-600">
                  {admins.filter(a => a.role === 'SUPER_ADMIN').length}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Admin List */}
      <div className="px-4 py-4 space-y-3">
        {admins.map((admin) => {
          const isLockedOut = admin.accountLockedUntil && new Date(admin.accountLockedUntil) > new Date();
          const canRevoke = admin.username !== 'mdaucodes910' && admin.username !== username;

          return (
            <Card key={admin.id} className={`border-l-4 ${
              !admin.isActive ? 'border-l-red-500' :
              admin.role === 'SUPER_ADMIN' ? 'border-l-purple-500' : 
              'border-l-teal-500'
            }`}>
              <CardContent className="p-4 space-y-3">
                {/* Header: Username + Role */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {admin.username}
                      </p>
                      {admin.username === username && (
                        <Badge variant="outline" className="text-xs">You</Badge>
                      )}
                    </div>
                    <Badge className={
                      admin.role === 'SUPER_ADMIN'
                        ? 'bg-purple-100 text-purple-700 border-purple-300'
                        : 'bg-blue-100 text-blue-700 border-blue-300'
                    }>
                      {admin.role === 'SUPER_ADMIN' ? (
                        <>
                          <ShieldAlert className="w-3 h-3 mr-1" />
                          Super Admin
                        </>
                      ) : (
                        <>
                          <Shield className="w-3 h-3 mr-1" />
                          Admin
                        </>
                      )}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    {admin.isActive ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                </div>

                {/* Status Info */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Status:</span>
                    <Badge variant={admin.isActive ? 'default' : 'destructive'}>
                      {admin.isActive ? 'Active' : 'Revoked'}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Last Login:</span>
                    <span className="text-gray-900 dark:text-white flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(admin.lastLogin)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Failed Attempts:</span>
                    <Badge variant={admin.failedLoginAttempts >= 3 ? 'destructive' : 'outline'}>
                      {admin.failedLoginAttempts}
                    </Badge>
                  </div>
                </div>

                {/* Lock Status */}
                {isLockedOut && (
                  <Alert className="bg-orange-50 border-orange-200">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-sm text-orange-700">
                      Account locked until {formatDate(admin.accountLockedUntil)}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Created Date */}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Created: {formatDate(admin.createdAt)}
                </p>

                {/* Actions */}
                {canRevoke && admin.isActive && (
                  <Button
                    variant="destructive"
                    size="sm"
                    className="w-full"
                    onClick={() => setRevokeConfirm(admin)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Revoke Access
                  </Button>
                )}

                {admin.username === 'mdaucodes910' && (
                  <Alert className="bg-yellow-50 border-yellow-200">
                    <Key className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-xs text-yellow-700">
                      Developer account - Cannot be revoked
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create Admin Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Admin</DialogTitle>
            <DialogDescription>
              Create a new admin account with selected role
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Username</Label>
              <Input
                placeholder="Enter username"
                value={newAdmin.username}
                onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
              />
            </div>

            <div>
              <Label>Password</Label>
              <Input
                type="password"
                placeholder="Min. 8 characters"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">
                Password must be at least 8 characters
              </p>
            </div>

            <div>
              <Label>Role</Label>
              <Select
                value={newAdmin.role}
                onValueChange={(val: any) => setNewAdmin({ ...newAdmin, role: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Admin (Regular)
                    </div>
                  </SelectItem>
                  <SelectItem value="SUPER_ADMIN">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4" />
                      Super Admin (Full Control)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateAdmin}
              disabled={isCreating}
              className="bg-teal-500 hover:bg-teal-600"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Admin
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation */}
      <Dialog open={!!revokeConfirm} onOpenChange={() => setRevokeConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Admin Access</DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke access for this admin?
            </DialogDescription>
          </DialogHeader>

          {revokeConfirm && (
            <Alert className="bg-red-50 border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-sm text-red-700">
                <strong>{revokeConfirm.username}</strong> will be immediately logged out and unable to access the admin panel.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRevokeConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevokeAdmin}
              disabled={isRevoking}
            >
              {isRevoking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Revoking...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Revoke Access
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}